/**
 * Client for triggering clinical docs RPA via queue.
 * @module clinicalDocsRpaTrigger
 */

import { FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE } from "../../constants/stringLiterals";
import { RPATaskRequest, RPATaskResponse } from "../../../medonc/data-model/rpaTaskQueue";
import { RPA_PATHS_ONLY_FOR_QUEUE } from "../../enums/apiUrls";
import { PortalIdForRabbitMq } from "../../enums/portalIdsForRabbitMq";
import { logDataToConsole } from "../../../medonc/utils/customLogger";
import { getBearerToken } from "./bearerToken";
import { sendRpaRequestsToRabbitMq } from "./sendRpaRequestsToRabbitMq";

/** Identifiers required to enqueue a clinical docs RPA task for an order. */
export interface ClinicalDocsRpaTriggerPayload {
  order_id: string;
  org_id: string;
}

/**
 * Builds the RPA task request payload for the clinical docs queue.
 *
 * @input payload - Order and org identifiers
 * @input token - Bearer token for authorization headers
 * @return RPATaskRequest - Fully constructed queue task request
 */
const buildRpaTaskRequest = (
  payload: ClinicalDocsRpaTriggerPayload,
  token: string,
): RPATaskRequest => ({
  payload: { order_id: payload.order_id },
  org_id: payload.org_id,
  portal_id: PortalIdForRabbitMq.ONCOEMR,
  priority: FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE,
  identifier: payload.order_id,
  order_id: payload.order_id,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Triggers clinical docs RPA via the RPA queue.
 * Enqueues the request via RabbitMQ with no direct API fallback.
 *
 * @input payload - Order and org identifiers for the clinical docs task
 * @return RPATaskResponse - Queue response indicating success or failure
 * @throws Error if the bearer token is unavailable or the queue request fails
 */
export const clinicalDocsRpaTrigger = async (
  payload: ClinicalDocsRpaTriggerPayload,
): Promise<RPATaskResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const rpaRequest = buildRpaTaskRequest(payload, token);
    return await sendRpaRequestsToRabbitMq(
      RPA_PATHS_ONLY_FOR_QUEUE.CLINICAL_DOCS,
      rpaRequest,
    );
  } catch (error) {
    logDataToConsole("error in clinicalDocsRpaTrigger", error);
    throw error;
  }
};
